import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import {
  isAllowedEnergyBusinessVertical,
  isAllowedInsuranceBusinessVertical,
  isAllowedTelecomBusinessVertical,
} from '@/containers/Login/AuthSelectors'

interface UseBusinessTypeReturn {
  type: string
  typeTranslated: string
  getApplicationUrl: (page: string) => string
  getApplicationMenuRoutes: (applicationType: string) => Record<string, string>
  getModulesStatuses: (applicationType: string) => Record<string, any>
  getApplicationTypeFolder: (type: string) => string
  translateBusinessTypeToUrl: (businessType: string) => string
  isAllowedEnergyVertical: any
  isAllowedTelecomVertical: any
  isAllowedInsuranceVertical: any
}

interface UseBusinessTypeParams {
  loaderRoutesEnergy?: Record<string, string>
  loaderRoutesInsurance?: Record<string, string>
  loaderRoutes?: Record<string, string>
  modulesStatuses?: Record<string, any>
  modulesStatusesEnergy?: Record<string, any>
  modulesStatusesInsurance?: Record<string, any>
}

const useBusinessType = ({
  loaderRoutesEnergy = {},
  loaderRoutesInsurance = {},
  loaderRoutes = {},
  modulesStatuses = {},
  modulesStatusesEnergy = {},
  modulesStatusesInsurance = {},
}: UseBusinessTypeParams): UseBusinessTypeReturn => {
  const { type = '' } = useParams()
  const isAllowedEnergyVertical = useSelector(isAllowedEnergyBusinessVertical)
  const isAllowedTelecomVertical = useSelector(isAllowedTelecomBusinessVertical)
  const isAllowedInsuranceVertical = useSelector(isAllowedInsuranceBusinessVertical)
  const getApplicationTypeFolder = (): string => {
    if (type === 'energie') {
      return 'Energy'
    }
    if (type === 'assurance') {
      return 'Insurance'
    }
    return 'Telecom'
  }
  const getApplicationRoutes = (): Record<string, string> => {
    if (type === 'energie') {
      return loaderRoutesEnergy ?? {}
    }
    if (type === 'assurance') {
      return loaderRoutesInsurance ?? {}
    }
    return loaderRoutes ?? {}
  }
  const getApplicationUrl = (page: string): string => {
    const applicationPaths = getApplicationRoutes()
    return applicationPaths[page] ?? ''
  }
  const getModulesStatuses = (applicationType: string): Record<string, any> => {
    if (applicationType === 'energie') {
      return modulesStatusesEnergy
    }
    if (applicationType === 'assurance') {
      return modulesStatusesInsurance
    }
    return modulesStatuses
  }

  const getApplicationMenuRoutes = (applicationType: string): Record<string, string> => {
    if (applicationType === 'energie') {
      return {
        home: '/energie',
        overview: '/energie/subscription/list/overview',
        clientCallbacks: '/callbacks',
        form: '/energie/subscription/add',
        users: '/users-mngt',
        ipAddresses: '/ip-addresses-management',
        offices: '/energie/offices',
        providers: '/energie/organisations/provider',
        servicesStatus: '/energie/services-status',
        devTools: '/energie/dev-tools/tools',
        assistanceProducts: '/energie/additional-sales/assistance',
        monConseillerPersoProducts: '/energie/additional-sales/mon-conseiller-perso',
        carbonOffset: '/energie/additional-sales/carbon-offset',
        additionalSales: '/additional-sales',
        logout: '/logout',
      }
    }
    if (applicationType === 'assurance') {
      return {
        home: '/assurance',
        overview: '/assurance/subscription/list/overview',
        clientCallbacks: '/callbacks',
        form: '/assurance/subscription/add',
        users: '/users-mngt',
        ipAddresses: '/ip-addresses-management',
        providers: '/assurance/organisations/provider',
        servicesStatus: '/assurance/services-status',
        devTools: '/assurance/dev-tools/tools',
        assistanceProducts: '/assurance/additional-sales/assistance',
        monConseillerPersoProducts: '/assurance/additional-sales/mon-conseiller-perso',
        carbonOffset: '/assurance/additional-sales/carbon-offset',
        additionalSales: '/additional-sales',
        logout: '/logout',
      }
    }
    return {
      home: '/telecom',
      overview: '/telecom/subscription/list/overview',
      clientCallbacks: '/callbacks',
      form: '/telecom/subscription/add',
      users: '/users-mngt',
      ipAddresses: '/ip-addresses-management',
      providers: '/telecom/organisations/provider',
      providerOptions: '/telecom/provider-options',
      servicesStatus: '/telecom/services-status',
      devTools: '/telecom/dev-tools/tools',
      assistanceProducts: '/telecom/additional-sales/assistance',
      monConseillerPersoProducts: '/telecom/additional-sales/mon-conseiller-perso',
      carbonOffset: '/telecom/additional-sales/carbon-offset',
      additionalSales: '/additional-sales',
      logout: '/logout',
    }
  }

  const translateBusinessTypeToUrl = (businessType: string): string => {
    if (businessType === 'energy') {
      return 'energie'
    }
    if (businessType === 'insurance') {
      return 'assurance'
    }
    return 'telecom'
  }

  useEffect(() => {
    if (!type) {
      document.title = 'HOP'
      return
    }

    document.title = 'HOP ' + type.toUpperCase()
  }, [])

  return {
    type,
    typeTranslated: getApplicationTypeFolder(),
    getApplicationUrl,
    getApplicationMenuRoutes,
    getModulesStatuses,
    getApplicationTypeFolder,
    translateBusinessTypeToUrl,
    isAllowedEnergyVertical,
    isAllowedTelecomVertical,
    isAllowedInsuranceVertical,
  }
}

export default useBusinessType
